import type {
  CanvasElement,
  TextElement,
  ImageElement,
  ButtonElement,
} from '../types/index';
import type { EnhancedFieldMapping } from '../types/mapping';
import type {
  ResolvedElement,
  AppliedMapping,
  ADACheckResult,
  ADACheckSeverity,
} from '../types/preview';
import { ADA_CHECK_RULES } from '../types/preview';
import { applyTransform } from '../stores/mappingStore';
import { applyMappedValue, applyTextFit } from './mappingValueApplicator';

export function resolveElements(
  elements: CanvasElement[],
  elementMappings: Record<string, EnhancedFieldMapping[]>,
  previewRecords: Record<string, unknown>[] | null,
  previewRecordIndex: number,
  previewTableName: string | null
): ResolvedElement[] {
  return elements.map((element) =>
    resolveElement(element, elementMappings, previewRecords, previewRecordIndex, previewTableName)
  );
}

function resolveElement(
  element: CanvasElement,
  elementMappings: Record<string, EnhancedFieldMapping[]>,
  previewRecords: Record<string, unknown>[] | null,
  previewRecordIndex: number,
  previewTableName: string | null
): ResolvedElement {
  if (element.contentType === 'STATIC') {
    return {
      original: element,
      resolved: element,
      appliedMappings: [],
      fullyResolved: true,
      errors: [],
    };
  }

  const mappings = elementMappings[element.id] || [];

  if (mappings.length === 0) {
    return {
      original: element,
      resolved: element,
      appliedMappings: [],
      fullyResolved: false,
      errors: ['No field mappings defined for this dynamic element'],
    };
  }

  const resolved = JSON.parse(JSON.stringify(element)) as CanvasElement;
  const appliedMappings: AppliedMapping[] = [];
  const errors: string[] = [];

  for (const mapping of mappings) {
    const { rawValue, resolvedValue, fromSource, error } = resolveMappingValue(
      mapping,
      previewRecords,
      previewRecordIndex,
      previewTableName
    );

    if (error) {
      errors.push(error);
    }

    appliedMappings.push({ mapping, rawValue, resolvedValue, fromSource });
    applyMappedValue(resolved, mapping.targetProperty, resolvedValue);
  }

  if (resolved.type === 'text') {
    applyTextFit(resolved as TextElement);
  }

  return {
    original: element,
    resolved,
    appliedMappings,
    fullyResolved: errors.length === 0,
    errors,
  };
}

function resolveMappingValue(
  mapping: EnhancedFieldMapping,
  previewRecords: Record<string, unknown>[] | null,
  previewRecordIndex: number,
  previewTableName: string | null
): {
  rawValue: string | null;
  resolvedValue: string;
  fromSource: boolean;
  error: string | null;
} {
  if (!previewRecords || previewRecords.length === 0 || mapping.tableName !== previewTableName) {
    return {
      rawValue: null,
      resolvedValue: mapping.fallbackValue || `{{${mapping.columnName}}}`,
      fromSource: false,
      error: mapping.tableName !== previewTableName
        ? `Preview data is for table "${previewTableName}", not "${mapping.tableName}"`
        : `No preview data loaded for table "${mapping.tableName}"`,
    };
  }

  const recordIndex = Math.min(previewRecordIndex, previewRecords.length - 1);
  const record = previewRecords[recordIndex];

  if (!record) {
    return {
      rawValue: null,
      resolvedValue: mapping.fallbackValue || `{{${mapping.columnName}}}`,
      fromSource: false,
      error: `Preview record at index ${previewRecordIndex} not found`,
    };
  }

  const rawValue = record[mapping.columnName];

  if (rawValue === null || rawValue === undefined) {
    return {
      rawValue: null,
      resolvedValue: mapping.fallbackValue || `{{${mapping.columnName}}}`,
      fromSource: false,
      error: null,
    };
  }

  let stringValue = String(rawValue);

  if (mapping.transformExpression) {
    stringValue = applyTransform(stringValue, mapping.transformExpression);
  }

  return {
    rawValue: stringValue,
    resolvedValue: stringValue,
    fromSource: true,
    error: null,
  };
}

export function runADAComplianceChecks(
  elements: CanvasElement[],
  resolvedElements: ResolvedElement[]
): ADACheckResult[] {
  const results: ADACheckResult[] = [];
  let checkId = 0;
  const nextId = () => `ada-check-${++checkId}`;

  const overlayElements = elements.filter(
    (el) => el.layer === 'OVERLAY_LAYER' && el.visible
  );
  if (overlayElements.length === 0) {
    results.push({
      id: nextId(),
      severity: ADA_CHECK_RULES.EMPTY_OVERLAY.severity,
      elementId: null,
      elementName: null,
      rule: ADA_CHECK_RULES.EMPTY_OVERLAY.rule,
      message: ADA_CHECK_RULES.EMPTY_OVERLAY.message,
      suggestion: ADA_CHECK_RULES.EMPTY_OVERLAY.suggestion,
    });
  }

  for (const re of resolvedElements) {
    const el = re.resolved;
    if (!el.visible) continue;

    if (el.type === 'text' && (el.layer as string) === 'BACKGROUND_LAYER') {
      results.push({
        id: nextId(),
        severity: ADA_CHECK_RULES.TEXT_IN_BACKGROUND.severity,
        elementId: el.id,
        elementName: el.name,
        rule: ADA_CHECK_RULES.TEXT_IN_BACKGROUND.rule,
        message: ADA_CHECK_RULES.TEXT_IN_BACKGROUND.message,
        suggestion: ADA_CHECK_RULES.TEXT_IN_BACKGROUND.suggestion,
      });
    }

    if (el.type === 'image' && el.layer === 'OVERLAY_LAYER') {
      const imgEl = el as ImageElement;
      if (!imgEl.alt || imgEl.alt.trim() === '') {
        results.push({
          id: nextId(),
          severity: ADA_CHECK_RULES.MISSING_ALT_TEXT.severity,
          elementId: el.id,
          elementName: el.name,
          rule: ADA_CHECK_RULES.MISSING_ALT_TEXT.rule,
          message: ADA_CHECK_RULES.MISSING_ALT_TEXT.message,
          suggestion: ADA_CHECK_RULES.MISSING_ALT_TEXT.suggestion,
        });
      }
    }

    if (el.type === 'button' && el.layer === 'OVERLAY_LAYER') {
      const btnEl = el as ButtonElement;
      if (!btnEl.href || btnEl.href.trim() === '') {
        results.push({
          id: nextId(),
          severity: ADA_CHECK_RULES.BUTTON_NO_HREF.severity,
          elementId: el.id,
          elementName: el.name,
          rule: ADA_CHECK_RULES.BUTTON_NO_HREF.rule,
          message: ADA_CHECK_RULES.BUTTON_NO_HREF.message,
          suggestion: ADA_CHECK_RULES.BUTTON_NO_HREF.suggestion,
        });
      }
    }

    if (el.type === 'text') {
      const textEl = el as TextElement;
      if (textEl.fontSize < 12) {
        results.push({
          id: nextId(),
          severity: ADA_CHECK_RULES.FONT_TOO_SMALL.severity,
          elementId: el.id,
          elementName: el.name,
          rule: ADA_CHECK_RULES.FONT_TOO_SMALL.rule,
          message: `Font size is ${textEl.fontSize}px — below 12px minimum`,
          suggestion: ADA_CHECK_RULES.FONT_TOO_SMALL.suggestion,
        });
      }
    }

    if (el.contentType === 'DYNAMIC' && !re.fullyResolved) {
      results.push({
        id: nextId(),
        severity: ADA_CHECK_RULES.DYNAMIC_UNRESOLVED.severity,
        elementId: el.id,
        elementName: el.name,
        rule: ADA_CHECK_RULES.DYNAMIC_UNRESOLVED.rule,
        message: `${re.errors.length} unresolved mapping(s): ${re.errors.join('; ')}`,
        suggestion: ADA_CHECK_RULES.DYNAMIC_UNRESOLVED.suggestion,
      });
    }
  }

  const severityOrder: Record<ADACheckSeverity, number> = { error: 0, warning: 1, info: 2 };
  results.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return results;
}
